<template>
  <hr v-if="separator" class="ak-dropdown__divider" @click.stop.prevent />
  <a
    v-else-if="!custom && !hasLink"
    class="ak-dropdown__item ak-dropdown__item--link"
    :class="anchorClasses"
    :role="ariaRoleItem"
    :tabindex="focusable ? 0 : null"
    @click.stop.prevent="selectItem($event)"
  >
    <slot />
  </a>
  <template v-else>
    <div
      v-if="isClickable"
      class="ak-dropdown__item"
      :class="itemClasses"
      :role="ariaRoleItem"
      :tabindex="focusable ? 0 : null"
      @click.stop.prevent="selectItem($event)"
    >
      <slot />
    </div>
    <div
      v-else
      class="ak-dropdown__item"
      :class="itemClasses"
      :role="ariaRoleItem"
      :tabindex="focusable ? 0 : null"
    >
      <slot />
    </div>
  </template>
</template>

<script>
import { defineComponent, computed, inject } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates.
   * @example
   * <ak-dropdown-item>One</ak-dropdown-item>
   */
  name: 'AkDropdownItem',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Mixed}
     * @description The value that will be returned on events and v-model
     */
    value: {
      type: [String, Number, Boolean, Object, Array, Function],
      default: null,
    },

    /**
     * @type {Boolean}
     * @description Set the item to be a separator
     */
    separator: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Item is disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Item is not a clickable item
     */
    custom: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Item can be focused
     */
    focusable: {
      type: Boolean,
      default: true,
    },

    /**
     * @type {Boolean}
     * @description Remove padding
     */
    flush: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Use it if your item is an anchor tag or router-link
     */
    hasLink: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Use if the dropdown is a dark style
     */
    dark: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description 	Role attribute to be passed to list item for better
     * accessibility. Use menuitem only in situations where your dropdown
     * is related to navigation menus.
     */
    ariaRole: {
      type: String,
      default: undefined,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['click'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const injected = inject('akDropdown')
    const parent = computed(() => injected)

    /**
     * @returns {Object}
     * @description Returns the anchor classes when rendering a anchor
     * dropdown item.
     */
    const anchorClasses = computed(() => {
      return {
        'ak-dropdown__item--flush': props.flush,
        'is-disabled': parent.value.disabled || props.disabled,
        'is-active': isActive.value,
        'ak-dropdown__item--dark': props.dark,
      }
    })

    /**
     * @returns {Object}
     * @description Returns the classes for the dropdown item.
     */
    const itemClasses = computed(() => {
      return {
        'ak-dropdown__item': !props.hasLink,
        'ak-dropdown__item--inner-link': props.hasLink,
        'ak-dropdown__item--flush': props.flush,
        'is-disabled': props.disabled,
        'is-active': isActive.value,
        'ak-dropdown__item--dark': props.dark,
      }
    })

    /**
     * @returns {String|Null}
     * @description Returns the "aria-role" property for the dropdown
     * item.
     */
    const ariaRoleItem = computed(() => {
      return props.ariaRole === 'menuitem' || props.ariaRole === 'listitem'
        ? props.ariaRole
        : null
    })

    /**
     * @returns {Boolean}
     * @description Check if item can be clickable.
     */
    const isClickable = computed(() => {
      if (parent.value.disabled) return false
      if (props.separator) return false
      if (props.disabled) return false
      if (props.custom) return false
      return true
    })

    /**
     * @returns {Boolean}
     * @description Returns the active state of the dropdown item
     */
    const isActive = computed(() => {
      if (parent.value.selected === null) {
        return false
      }

      if (parent.value.multiple) {
        return parent.value.selected.indexOf(props.value) >= 0
      }

      return props.value === parent.value.selected
    })

    /**
     * @description Click listener, select the item.
     */
    const selectItem = (e) => {
      if (!isClickable.value) return
      else {
        parent.value.selectItem(props.value)
        emit('click', e)
      }
    }

    return {
      anchorClasses,
      itemClasses,
      ariaRoleItem,
      isClickable,
      isActive,
      selectItem,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-dropdown {
  &__item {
    $item: &;
    padding-top: $spacing-tiny;
    padding-bottom: $spacing-tiny;
    padding-left: $spacing;
    padding-right: $spacing;
    display: block;
    align-items: center;
    outline: 0;
    text-align: left;

    &--link {
      cursor: pointer;
      &:focus {
        background-color: $color-grey-lightest;
        color: #000;
      }
      &#{$item}--dark {
        &:focus {
          background-color: $color-grey-darker;
          color: #fff;
        }
      }
    }
    &--link,
    &--inner-link {
      &,
      a {
        cursor: pointer;
        color: $color-text;
      }
      &:hover {
        &,
        a {
          color: $color-primary !important;
        }
      }
      &#{$item}--dark {
        &,
        a {
          color: rgba(255, 255, 255, 0.8);
        }
      }
      &#{$item}--dark:hover {
        &,
        a {
          &:hover {
            color: #fff;
          }
        }
      }
    }

    &--flush {
      padding-left: 0;
      padding-right: 0;
    }

    // state modifiers
    &.is-disabled {
      cursor: not-allowed;
      &:hover {
        color: inherit;
      }
    }

    &.is-active {
      font-weight: bold;
      color: $color-primary;
    }
  }

  &__divider {
    border-bottom: 1px solid $color-grey-lighter;
    margin: $spacing-small 0;
  }
}
</style>
